/* SpawnAnything — unit groups on the client (pure, shared by main.js, multiplayer, replays and the tests).
   A battle has up to MAX_TROOPS troops per team ("mixed armies", user decision 02.10.2026). Each troop is one unit group
   = one unit type with its own index range, exactly as the worker lays them out (src/sim/sim.worker.js setup):
   groups sorted by team, inside a team by troop slot; the last group of a team also owns the team's reserve slots.
   Painted units are stored per team (paint[t] = [x, z, ...]) with the troop slot of every position in a parallel list
   (paintS[t][k], missing = slot 0). */

export const MAX_TROOPS = 4;

/** Setup configs -> groups: classic [cfgBlue, cfgRed] (no `team`) become one group per team, slot 0. Sorted by team, then
    `order` (if given) or slot. */
export function normalizeGroups(cfgs) {
  const list = cfgs.map((c, k) => ({ ...c, team: c.team === 0 || c.team === 1 ? c.team : k ? 1 : 0, slot: Number.isInteger(c.slot) ? c.slot : 0 }));
  const key = c => (Number.isFinite(c.order) ? c.order : c.slot); // optional order (e.g. zombies last), else the slot
  return list.map((c, k) => [c, k]).sort((a, b) => a[0].team - b[0].team || key(a[0]) - key(b[0]) || a[1] - b[1]).map(e => e[0]);
}

/** Index ranges like the worker: counts[g] units per group (sorted), reserve[t] spawn slots on each team's last group.
    Returns { start, resStart, end } per group, the first index of team 1 (split), N and the group of every unit. */
export function groupRanges(teams, counts, reserve = [0, 0]) {
  const G = teams.length, last = [-1, -1];
  for (let g = 0; g < G; g++) last[teams[g]] = g;
  const start = new Array(G), resStart = new Array(G), end = new Array(G);
  let off = 0, split = 0;
  for (let t = 0; t < 2; t++) {
    if (t === 1) split = off;
    for (let g = 0; g < G; g++) {
      if (teams[g] !== t) continue;
      start[g] = off; off += counts[g] | 0; resStart[g] = off;
      if (g === last[t]) off += reserve[t] | 0;
      end[g] = off;
    }
  }
  const GRP = new Uint8Array(off);
  for (let g = 0; g < G; g++) GRP.fill(g, start[g], end[g]);
  return { start, resStart, end, split, N: off, GRP };
}

/** Painted positions per group (groups sorted, each { team, slot }): Float32Arrays in paint order, plus the unit index of
    every painted position (unitOf[t][k], -1 = its troop is not in the battle). counts[g] = painted units of group g. */
export function paintGroups(groups, paint, paintS, starts) {
  const pos = groups.map(() => []), unitOf = [new Int32Array(paint[0].length / 2).fill(-1), new Int32Array(paint[1].length / 2).fill(-1)];
  const gOf = [new Map(), new Map()];
  groups.forEach((A, g) => { if (!gOf[A.team].has(A.slot)) gOf[A.team].set(A.slot, g); });
  for (let t = 0; t < 2; t++) {
    const a = paint[t], s = paintS[t] || [];
    for (let k = 0; k < a.length / 2; k++) {
      const g = gOf[t].get(s[k] | 0); if (g === undefined) continue;
      if (starts) unitOf[t][k] = starts[g] + pos[g].length / 2;
      pos[g].push(a[2 * k], a[2 * k + 1]);
    }
  }
  return { positions: pos.map(p => new Float32Array(p)), counts: pos.map(p => p.length / 2), unitOf };
}

/** Keep only the painted positions for which keep(t, k) is true (both lists stay parallel). */
export function filterPaint(paint, paintS, t, keep) {
  const a = paint[t], s = paintS[t] || [], out = [], os = [];
  for (let k = 0; k < a.length / 2; k++) if (keep(k)) { out.push(a[2 * k], a[2 * k + 1]); os.push(s[k] | 0); }
  paint[t] = out; paintS[t] = os;
}
